import React, { useState, useEffect, useCallback } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useNavigate } from "react-router-dom";
import { getter } from "@progress/kendo-react-common";
import { useSelector, useDispatch } from "react-redux";
import CustomCellClick from "../Grid/CustomCellClick";
import { useLoadClientGridData } from "../../handlers/client";

import { useKendoGridHandlers } from "../../CustomHooks/useKendoGridHandlers";
import { setCurrentPage } from "../../store/reducers/clientFilterSlice";
import useHandlePageChange from "../../CustomHooks/useHandlePageChange";
import {
  setCurrentClientId,
  setCurrentEntityId,
  setSearchFrom,
} from "../../store/reducers/SharedLayoutDataSlice";
import Loader from "../../Shared/Loader";
import useQueryReloadButton from "../../CustomHooks/useQueryReloadButton";
import { DisplayMsg } from "../../services/toastService";

const DATA_ITEM_KEY = "authEntityId";
const SELECTED_FIELD = "selected";

const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO = {
  action: "SEARCHCLIENTS",
  className: "CLIENT",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "PRACTICE_NAME",
  sortOrder: "desc",
  gridHeight: "300px",
};

const PAGER_SETTINGS = {
  info: true,
  type: "input",
  previousNext: true,
  pageSize: 4,
  refresh: true,
};

const checkSelectedContentLength = () => {
  const selectedText = window.getSelection().toString();
  return selectedText.length > 0;
};

const KendoClientGrid = React.memo(() => {
  const [localGridData, setLocalGridData] = useState(null);
  const dispatch = useDispatch();
  const clientFilter = useSelector((state) => state.clientFilter);

  const navigate = useNavigate();

  const [gridState, setGridState] = useState({
    pageNumber: DEFAULT_GRID_INFO.currentPageNumber,
    pageSize: DEFAULT_GRID_INFO.pageSize,
    sortName: DEFAULT_GRID_INFO.sortName,
    sortOrder: DEFAULT_GRID_INFO.sortOrder,
    sort: [
      {
        field: DEFAULT_GRID_INFO.sortName,
        dir: DEFAULT_GRID_INFO.sortOrder?.toLowerCase() || "asc",
      },
    ],
  });

  const HandlePageChange = useHandlePageChange(setGridState, setCurrentPage);

  const { HandleDataStateChange } = useKendoGridHandlers(setGridState);

  const [selectedState, setSelectedState] = React.useState();

  const {
    data: gridData,
    isLoading,
    refetch,
    isRefetching,
  } = useLoadClientGridData(clientFilter, gridState);

  useEffect(() => {
    if (gridData && !gridData?.succeeded) {
      DisplayMsg("2", gridData?.message);
    } else if (gridData?.data?.rows) {
      setLocalGridData(gridData.data);
    }
  }, [gridData]);

  useQueryReloadButton(isLoading, isRefetching, refetch);

  const CustomCellClicked = useCallback(
    (cellProps) => (
      <CustomCellClick
        {...cellProps}
        ParentCallback={() => {}}
        {...cellProps.additionalProps}
      />
    ),
    []
  );

  const onSelectionChange = React.useCallback(
    (event) => {
      const check = checkSelectedContentLength();
      if (!check) {
        const newSelectedState = getSelectedState({
          event,
          selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
        const currentColumn = event.target._columns[event.endColIndex].field;
        if (currentColumn === "practiceName") {
          const currentClient = event.dataItems[event.endRowIndex];
          dispatch(setCurrentClientId(currentClient.authEntityId));
          dispatch(setCurrentEntityId(currentClient.entityId));
          dispatch(setSearchFrom("client"));
          navigate("/client-detail", {
            state: {
              ClientId: currentClient.authEntityId,
              EntityId: currentClient.entityId,
              CurrentTab: "ClientDetail",
            },
          });
        }
      }
    },
    [selectedState, dispatch, navigate]
  );

  const CellSentenceCase = (props) => {
    const fieldName =
      props?.dataItem[props.field] != null
        ? props.dataItem[props.field]
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : null;

    return (
      <td className="Comments30 pl-4" title={fieldName}>
        {fieldName == "- Select -" ? "" : fieldName}
      </td>
    );
  };

  // if (isLoading) return <Loader />;

  return (
    <div>
      {isLoading && <Loader />}

      <Tooltip openDelay={100} position="right" anchorElement="target">
        <Grid
          style={{
            height: "calc(100vh - 175px)",
          }}
          scrollable="scrollable"
          total={localGridData?.total}
          skip={(gridState.pageNumber - 1) * gridState.pageSize}
          pageSize={gridState.pageSize}
          filterable={false}
          sortable={{
            mode: "single",
            allowUnsort: false,
          }}
          sort={gridState.sort}
          selectedField={SELECTED_FIELD}
          data={localGridData?.rows.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
          }))}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "multiple",
          }}
          pageable={PAGER_SETTINGS}
          onPageChange={HandlePageChange}
          onDataStateChange={HandleDataStateChange}
          onSelectionChange={onSelectionChange}
        >
          <GridColumn
            title="Customer Id"
            field="authEntityId"
            sortable
            width={140}
          />
          <GridColumn
            title="Practice Name"
            field="practiceName"
            sortable
            cell={CustomCellClicked}
            width={160}
          />
          <GridColumn title="Entity" field="entityShortName" width={170} />
          <GridColumn
            title="Speciality"
            field="speciality"
            className="pl-4"
            cell={CellSentenceCase}
            filterable={false}
            width={180}
          />
          <GridColumn
            title="Email"
            field="practiceEmail"
            className="pl-4"
            filterable={false}
            width={220}
          />
          <GridColumn
            title="Telephone"
            field="practiceTelNum"
            className="pl-4"
            filterable={false}
            width={160}
          />
          <GridColumn
            title="Contact Person"
            field="contactPerson"
            cell={CellSentenceCase}
            className="pl-4"
            sortable
            width={160}
          />
          <GridColumn
            title="Channel Partner"
            field="channelPartner"
            className="pl-4"
            sortable
            filterable={false}
            width={160}
          />
          <GridColumn
            title="Sales Person"
            field="salesPerson"
            cell={CellSentenceCase}
            className="pl-4"
            width={160}
          />
          <GridColumn
            title="Trial Activation Status"
            field="trialActivationStatus"
            cell={CellSentenceCase}
            width={210}
            className="pl-4"
          />
          <GridColumn
            title="Account Type"
            field="accountType"
            cell={CellSentenceCase}
            className="pl-4"
            width={160}
          />
        </Grid>
      </Tooltip>
    </div>
  );
});
export default KendoClientGrid;
